'use client';

import React, { useState, useTransition } from 'react';
import Link from 'next/link';
import styles from './forgot-password.module.scss';
import { useTranslation } from '@/hooks/useTranslation';
import { getPasswordPolicy } from '@/lib/passwordConfig';

// Steps: email → questions (or otp-send) → reset (or otp-verify+reset) → done
type Step = 'email' | 'questions' | 'reset' | 'otp-send' | 'otp-verify' | 'done';
type Mode = 'questions' | 'otp'; // which recovery path the user is on

interface SecurityQuestions {
  question1: string;
  question2: string;
}

const EyeIcon = ({ open }: { open: boolean }) =>
  open ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

// ── PasswordField: defined at module scope to prevent focus loss on re-render ──
// If defined inside the parent component, React sees a new function type on every
// render, causing it to unmount + remount the input (losing focus after each keypress).
interface PasswordFieldProps {
  id: string;
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggle: () => void;
  label: string;
  stylesField: string;
  stylesLabel: string;
  stylesInput: string;
}

const PasswordField = ({ id, value, onChange, show, onToggle, label, stylesField, stylesLabel, stylesInput }: PasswordFieldProps) => (
  <div className={stylesField}>
    <label className={stylesLabel} htmlFor={id}>{label}</label>
    <div style={{ position: 'relative' }}>
      <input
        id={id}
        type={show ? 'text' : 'password'}
        required
        minLength={6}
        className={stylesInput}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ paddingRight: '44px' }}
      />
      <button
        type="button"
        onClick={onToggle}
        style={{ position: 'absolute', top: '50%', right: '12px', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '4px' }}
        aria-label={show ? 'Ocultar' : 'Mostrar'}
      >
        <EyeIcon open={show} />
      </button>
    </div>
  </div>
);

export default function ForgotPasswordPage() {
  const [step, setStep] = useState<Step>('email');
  const [mode, setMode] = useState<Mode>('questions');
  const [email, setEmail] = useState('');
  const [questions, setQuestions] = useState<SecurityQuestions | null>(null);
  const [answer1, setAnswer1] = useState('');
  const [answer2, setAnswer2] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [error, setError] = useState('');
  const [isPending, startTransition] = useTransition();

  const { t } = useTranslation();
  const { regex: PASSWORD_REGEX } = getPasswordPolicy();

  // ── Step 1: look up security questions ─────────────────────────────────────
  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    startTransition(async () => {
      try {
        const r = await fetch(`/api/v1/auth/forgot-password?email=${encodeURIComponent(email)}`);
        const j = await r.json();
        if (!j.success) {
          setError(t('auth.forgotPassword.requestError'));
          return;
        }
        if (!j.data.found) {
          setError(t('auth.forgotPassword.notFound'));
          return;
        }
        if (j.data.noSecurityQuestions) {
          // No security questions - go straight to OTP mode
          setMode('otp');
          setStep('otp-send');
          return;
        }
        setQuestions({ question1: j.data.question1, question2: j.data.question2 });
        setMode('questions');
        setStep('questions');
      } catch {
        setError(t('auth.forgotPassword.requestError'));
      }
    });
  };

  // ── Step 2a: security questions answered → password reset ──────────────────
  const handleQuestionsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!answer1.trim() || !answer2.trim()) {
      setError('Por favor responda a ambas as perguntas de segurança.');
      return;
    }
    setStep('reset');
  };

  // ── Step 2b: request OTP email ─────────────────────────────────────────────
  const handleSendOtp = async () => {
    setError('');
    startTransition(async () => {
      try {
        const r = await fetch('/api/v1/auth/forgot-password/otp', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        const j = await r.json();
        if (!j.success) {
          if (j.code === 'OTP_RATE_LIMIT') {
            setError(t('auth.forgotPassword.otpRateLimit'));
          } else {
            setError(t('auth.forgotPassword.requestError'));
          }
          return;
        }
        setOtpSent(true);
        setStep('otp-verify');
      } catch {
        setError(t('auth.forgotPassword.requestError'));
      }
    });
  };

  // ── Step 3a: reset via security answers ────────────────────────────────────
  const handleResetWithAnswers = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPass !== confirmPass) { setError(t('auth.errors.passwordMismatch')); return; }
    if (!PASSWORD_REGEX.test(newPass)) { setError(t('auth.errors.weakPassword')); return; }
    startTransition(async () => {
      try {
        const r = await fetch('/api/v1/auth/forgot-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, answer1, answer2, newPassword: newPass }),
        });
        const j = await r.json();
        if (j.success) {
          setStep('done');
        } else {
          setError(
            j.code === 'INVALID_ANSWERS'
              ? t('auth.forgotPassword.wrongAnswers')
              : j.code === 'NO_SECURITY_QUESTIONS'
                ? t('auth.forgotPassword.noSecurityQuestionsError')
                : t('auth.errors.genericError'),
          );
        }
      } catch {
        setError(t('auth.forgotPassword.requestError'));
      }
    });
  };

  // ── Step 3b: reset via OTP ─────────────────────────────────────────────────
  const handleResetWithOtp = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPass !== confirmPass) { setError(t('auth.errors.passwordMismatch')); return; }
    if (!PASSWORD_REGEX.test(newPass)) { setError(t('auth.errors.weakPassword')); return; }
    if (otpCode.length !== 6) { setError(t('auth.forgotPassword.otpInvalid')); return; }
    startTransition(async () => {
      try {
        const r = await fetch('/api/v1/auth/forgot-password/otp', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, code: otpCode, newPassword: newPass }),
        });
        const j = await r.json();
        if (j.success) {
          setStep('done');
        } else {
          setError(
            (j.code === 'INVALID_OTP' || j.code === 'OTP_EXPIRED')
              ? t('auth.forgotPassword.otpInvalid')
              : t('auth.errors.genericError'),
          );
        }
      } catch {
        setError(t('auth.forgotPassword.requestError'));
      }
    });
  };

  // Which steps count as progress markers
  const progressSteps: Step[] =
    mode === 'otp'
      ? ['email', 'otp-send', 'otp-verify']
      : ['email', 'questions', 'reset'];

  const progressIndex = progressSteps.indexOf(step);


  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {/* Logo */}
        <div className={styles.logo}>
          <Link href="/" className={styles.logoLink}>
            <img src="/logo.png" alt="Autonomo" style={{ height: '36px', width: 'auto' }} />
          </Link>
        </div>

        {/* Progress dots */}
        {step !== 'done' && (
          <div className={styles.steps}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`${styles.stepDot} ${progressIndex === i ? styles.stepActive : progressIndex > i ? styles.stepDone : ''}`}
              />
            ))}
          </div>
        )}

        {/* ── Step 1: Email ───────────────────────────────────────────────── */}
        {step === 'email' && (
          <>
            <h1 className={styles.title}>{t('auth.forgotPassword.title')}</h1>
            <p className={styles.subtitle}>{t('auth.forgotPassword.subtitle')}</p>
            <form onSubmit={handleEmailSubmit} className={styles.form}>
              {error && <div className={styles.error}>{error}</div>}
              <div className={styles.field}>
                <label className={styles.label} htmlFor="fp-email">{t('auth.forgotPassword.emailLabel')}</label>
                <input
                  id="fp-email"
                  type="email"
                  required
                  className={styles.input}
                  placeholder="o-seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <button type="submit" className={styles.btn} disabled={isPending}>
                {isPending ? <span className={styles.spinner} /> : null}
                {isPending ? t('common.loading') : t('auth.forgotPassword.submitButton')}
              </button>
            </form>
          </>
        )}

        {/* ── Step 2a: Security Questions ─────────────────────────────────── */}
        {step === 'questions' && questions && (
          <>
            <h1 className={styles.title}>{t('auth.forgotPassword.title')}</h1>
            <p className={styles.subtitle}>{t('auth.forgotPassword.subtitle')}</p>
            <form onSubmit={handleQuestionsSubmit} className={styles.form}>
              {error && <div className={styles.error}>{error}</div>}
              <div className={styles.field}>
                <label className={styles.label}>{questions.question1}</label>
                <input
                  type="text"
                  required
                  className={styles.input}
                  value={answer1}
                  onChange={(e) => setAnswer1(e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>{questions.question2}</label>
                <input
                  type="text"
                  required
                  className={styles.input}
                  value={answer2}
                  onChange={(e) => setAnswer2(e.target.value)}
                />
              </div>
              <button type="submit" className={styles.btn} disabled={isPending}>
                {t('auth.forgotPassword.submitButton')}
              </button>
              {/* "Forgot answers" → switch to OTP */}
              <button
                type="button"
                className={styles.link}
                onClick={() => { setError(''); setMode('otp'); setStep('otp-send'); }}
              >
                {t('auth.forgotPassword.forgotAnswers')}
              </button>
              <button type="button" className={styles.link} onClick={() => setStep('email')}>
                ← {t('common.back')}
              </button>
            </form>
          </>
        )}

        {/* ── Step 2b: OTP send confirmation ─────────────────────────────── */}
        {step === 'otp-send' && (
          <>
            <h1 className={styles.title}>{t('auth.forgotPassword.title')}</h1>
            <p className={styles.subtitle}>{t('auth.forgotPassword.otpSubtitle')}</p>
            <div className={styles.form}>
              {error && <div className={styles.error}>{error}</div>}
              <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '16px', textAlign: 'center' }}>
                {email}
              </p>
              <button className={styles.btn} onClick={handleSendOtp} disabled={isPending}>
                {isPending ? <span className={styles.spinner} /> : null}
                {isPending ? t('common.loading') : t('auth.forgotPassword.sendOtp')}
              </button>
              {questions && (
                <button
                  type="button"
                  className={styles.link}
                  onClick={() => { setError(''); setMode('questions'); setStep('questions'); }}
                >
                  {t('auth.forgotPassword.useSecurityQuestions')}
                </button>
              )}
              <button type="button" className={styles.link} onClick={() => setStep('email')}>
                ← {t('common.back')}
              </button>
            </div>
          </>
        )}

        {/* ── Step 2c/3b: OTP verify + new password ──────────────────────── */}
        {step === 'otp-verify' && (
          <>
            <h1 className={styles.title}>{t('auth.forgotPassword.title')}</h1>
            <p className={styles.subtitle}>{t('auth.forgotPassword.otpSent')}</p>
            <form onSubmit={handleResetWithOtp} className={styles.form}>
              {error && <div className={styles.error}>{error}</div>}
              <div className={styles.field}>
                <label className={styles.label} htmlFor="fp-otp">{t('auth.forgotPassword.otpLabel')}</label>
                <input
                  id="fp-otp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  className={styles.input}
                  placeholder={t('auth.forgotPassword.otpPlaceholder')}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  style={{ letterSpacing: '0.25em', fontSize: '1.2rem', textAlign: 'center' }}
                />
              </div>
              <PasswordField
                id="fp-otp-newpass"
                value={newPass}
                onChange={setNewPass}
                show={showNewPass}
                onToggle={() => setShowNewPass(v => !v)}
                label={t('auth.forgotPassword.newPasswordLabel')}
                stylesField={styles.field}
                stylesLabel={styles.label}
                stylesInput={styles.input}
              />
              <PasswordField
                id="fp-otp-confirm"
                value={confirmPass}
                onChange={setConfirmPass}
                show={showConfirmPass}
                onToggle={() => setShowConfirmPass(v => !v)}
                label={t('auth.forgotPassword.confirmNewPasswordLabel')}
                stylesField={styles.field}
                stylesLabel={styles.label}
                stylesInput={styles.input}
              />
              <button type="submit" className={styles.btn} disabled={isPending}>
                {isPending ? <span className={styles.spinner} /> : null}
                {isPending ? t('common.loading') : t('auth.forgotPassword.verifyAndReset')}
              </button>
              <button type="button" className={styles.link} onClick={handleSendOtp} disabled={isPending}>
                {t('auth.forgotPassword.resendOtp')}
              </button>
            </form>
          </>
        )}

        {/* ── Step 3a: New Password (security answers path) ──────────────── */}
        {step === 'reset' && (
          <>
            <h1 className={styles.title}>{t('auth.forgotPassword.newPasswordLabel')}</h1>
            <p className={styles.subtitle}>{t('auth.forgotPassword.subtitle')}</p>
            <form onSubmit={handleResetWithAnswers} className={styles.form}>
              {error && <div className={styles.error}>{error}</div>}
              <PasswordField
                id="fp-newpass"
                value={newPass}
                onChange={setNewPass}
                show={showNewPass}
                onToggle={() => setShowNewPass(v => !v)}
                label={t('auth.forgotPassword.newPasswordLabel')}
                stylesField={styles.field}
                stylesLabel={styles.label}
                stylesInput={styles.input}
              />
              <PasswordField
                id="fp-confirm"
                value={confirmPass}
                onChange={setConfirmPass}
                show={showConfirmPass}
                onToggle={() => setShowConfirmPass(v => !v)}
                label={t('auth.forgotPassword.confirmNewPasswordLabel')}
                stylesField={styles.field}
                stylesLabel={styles.label}
                stylesInput={styles.input}
              />
              <button type="submit" className={styles.btn} disabled={isPending}>
                {isPending ? <span className={styles.spinner} /> : null}
                {isPending ? t('common.loading') : t('auth.forgotPassword.resetButton')}
              </button>
            </form>
          </>
        )}

        {/* ── Done ───────────────────────────────────────────────────────── */}
        {step === 'done' && (
          <div className={styles.success}>
            <div className={styles.successIcon}>✓</div>
            <h1 className={styles.title}>{t('auth.forgotPassword.successMessage')}</h1>
            <p className={styles.subtitle}>{t('auth.forgotPassword.successMessage')}</p>
            <Link href="/login" className={styles.btn} style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
              {t('auth.forgotPassword.backToLogin')}
            </Link>
          </div>
        )}

        <div className={styles.footer}>
          <Link href="/login" className={styles.footerLink}>{t('auth.forgotPassword.backToLogin')}</Link>
        </div>
      </div>
    </div>
  );
}
